import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  ListChecks, Phone as PhoneIcon, EnvelopeSimple, VideoCamera, NotePencil,
  Check, Circle, Trash, Plus, FunnelSimple, User
} from '@phosphor-icons/react';
import { WatchHowLink } from '../components/WatchHowLink';
import { DatePicker, TimePicker, todayISO, nextHalfHourSlot } from '../components/DateTimePickers';

const TYPE_ICONS = { call: PhoneIcon, email: EnvelopeSimple, meeting: VideoCamera, note: NotePencil, todo: ListChecks };
const TYPE_COLORS = {
  call: 'text-green-600', email: 'text-blue-600', meeting: 'text-purple-600', note: 'text-amber-600', todo: 'text-gray-600',
};
const PRIORITY_BADGES = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-gray-100 text-gray-500 border-gray-200',
};

export default function TaskListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', activity_type: 'todo', scheduled_date: '', scheduled_time: '',
    priority: 'medium', content: '', contact_id: '',
  });
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = { completed: showCompleted ? 'all' : 'false' };
      if (typeFilter) params.activity_type = typeFilter;
      if (priorityFilter) params.priority = priorityFilter;
      const { data } = await api.get('/tasks', { params });
      setTasks(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Auto-open the New Task dialog if ?date=YYYY-MM-DD is in URL (from Calendar)
  useEffect(() => {
    const presetDate = searchParams.get('date');
    if (presetDate) {
      setForm({
        title: '', activity_type: 'todo', scheduled_date: presetDate,
        scheduled_time: nextHalfHourSlot(), priority: 'medium', content: '', contact_id: '',
      });
      setSelectedContact(null);
      setDialogOpen(true);
      // Clear the param so reopening works as expected
      searchParams.delete('date');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchTasks(); }, [showCompleted, typeFilter, priorityFilter]);

  const toggleComplete = async (activityId) => {
    try {
      await api.put(`/activities/${activityId}/complete`);
      fetchTasks();
    } catch {}
  };

  const deleteTask = async (activityId) => {
    try {
      await api.delete(`/activities/${activityId}`);
      fetchTasks();
    } catch {}
  };

  const searchContacts = async (q) => {
    setContactSearch(q);
    if (q.length < 2) { setContactResults([]); return; }
    try {
      const { data } = await api.get('/contacts/search/quick', { params: { q } });
      setContactResults(data);
    } catch { setContactResults([]); }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) {
      alert('Please add a task title');
      return;
    }
    setSaving(true);
    try {
      await api.post('/tasks', form);
      setDialogOpen(false);
      setForm({ title: '', activity_type: 'todo', scheduled_date: '', scheduled_time: '', priority: 'medium', content: '', contact_id: '' });
      setSelectedContact(null);
      fetchTasks();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create task');
    }
    finally { setSaving(false); }
  };

  const pendingCount = tasks.filter(t => !t.completed).length;
  const overdueCount = tasks.filter(t => !t.completed && t.scheduled_date && t.scheduled_date < new Date().toISOString().substring(0, 10)).length;

  // Build an ordered, de-duped contact_id list from the currently visible tasks.
  // Used so that clicking a task opens its contact AND seeds Next/Prev to walk
  // through the rest of the filtered task list (ACT! 4.0 lookup behavior).
  const buildTaskLookup = () => {
    const seen = new Set();
    const ids = [];
    for (const t of tasks) {
      if (t.contact_id && !seen.has(t.contact_id)) {
        seen.add(t.contact_id);
        ids.push(t.contact_id);
      }
    }
    return ids;
  };

  const openTaskContact = (t) => {
    if (!t.contact_id) return;
    const ids = buildTaskLookup();
    // Compose a friendly label that mirrors the active filters
    const labelParts = [];
    if (typeFilter) labelParts.push(typeFilter[0].toUpperCase() + typeFilter.slice(1) + 's');
    if (priorityFilter) labelParts.push(priorityFilter[0].toUpperCase() + priorityFilter.slice(1));
    labelParts.push(showCompleted ? 'All Tasks' : 'Open Tasks');
    const label = `Task List · ${labelParts.join(' · ')}`;
    navigate(`/contact?id=${t.contact_id}`, {
      state: {
        lookupContactIds: ids,
        lookupLabel: label,
        targetContactId: t.contact_id,
      },
    });
  };

  return (
    <div className="flex flex-col h-full" data-testid="tasklist-page">
      {/* Toolbar */}
      <div className="bg-gradient-to-b from-[#E8E8E8] to-[#D0D0D0] border-b border-[#999] px-3 py-1.5 flex items-center gap-2 shrink-0">
        <ListChecks size={16} weight="bold" className="text-[#0B5394]" />
        <span className="text-[13px] font-bold text-[#333]">Task List</span>
        <span className="text-[10px] text-[#666] ml-1">{pendingCount} pending{overdueCount > 0 ? `, ${overdueCount} overdue` : ''}</span>
        <WatchHowLink slug="tasks" className="ml-2" />
        <div className="flex-1" />
        <button onClick={() => {
          setForm({
            title: '', activity_type: 'todo', scheduled_date: todayISO(),
            scheduled_time: nextHalfHourSlot(), priority: 'medium', content: '', contact_id: '',
          });
          setSelectedContact(null);
          setDialogOpen(true);
        }} className="act-toolbar-btn" data-testid="create-task-btn">
          <Plus size={14} weight="bold" />
          <span className="text-[10px]">New Task</span>
        </button>
      </div>

      <div className="flex-1 overflow-auto p-2 bg-[#E8E8E8]">
        <div className="bg-[#F5F5F0] border border-[#999]">
          {/* Filters */}
          <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999] flex items-center gap-3 flex-wrap">
            <span className="text-[10px] font-bold text-[#333]"><FunnelSimple size={10} weight="bold" className="inline mr-1" />Filters:</span>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
              className="h-[24px] px-1.5 text-[10px] border border-[#B0B0B0] bg-white" data-testid="task-type-filter">
              <option value="">All Types</option>
              <option value="call">Calls</option>
              <option value="meeting">Meetings</option>
              <option value="todo">To-Do</option>
              <option value="email">Emails</option>
              <option value="note">Notes</option>
            </select>
            <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}
              className="h-[24px] px-1.5 text-[10px] border border-[#B0B0B0] bg-white" data-testid="task-priority-filter">
              <option value="">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} className="w-3 h-3" />
              <span className="text-[10px] text-[#333]">Show completed</span>
            </label>
          </div>

          {/* Task Table */}
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-[#0B5394] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : tasks.length === 0 ? (
            <p className="text-[12px] text-[#999] text-center py-12">No tasks found. Schedule activities from the Contact card or click New Task.</p>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-[11px] min-w-[600px]" data-testid="task-table">
              <thead>
                <tr className="bg-[#E0E0E0] border-b border-[#999]">
                  <th className="w-8 px-2 py-1.5"></th>
                  <th className="px-2 py-1.5 text-left font-semibold text-[#333]">Type</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-[#333]">Date</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-[#333] hidden sm:table-cell">Time</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-[#333] hidden sm:table-cell">Priority</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-[#333]">Regarding</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-[#333] hidden md:table-cell">Contact</th>
                  <th className="px-2 py-1.5 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(t => {
                  const Icon = TYPE_ICONS[t.activity_type] || NotePencil;
                  const isOverdue = !t.completed && t.scheduled_date && t.scheduled_date < new Date().toISOString().substring(0, 10);
                  return (
                    <tr key={t.activity_id} className={`border-b border-[#DDD] hover:bg-[#E8E8E8] ${t.completed ? 'opacity-50' : ''} ${isOverdue ? 'bg-red-50' : ''}`}
                      data-testid={`task-row-${t.activity_id}`}>
                      <td className="px-2 py-1.5 text-center">
                        <button onClick={() => toggleComplete(t.activity_id)} className="p-0.5" data-testid={`task-toggle-${t.activity_id}`}>
                          {t.completed ? <Check size={14} weight="bold" className="text-green-600" /> : <Circle size={14} weight="bold" className="text-[#B0B0B0]" />}
                        </button>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`flex items-center gap-1 capitalize ${TYPE_COLORS[t.activity_type]}`}>
                          <Icon size={12} weight="bold" />{t.activity_type}
                        </span>
                      </td>
                      <td className={`px-2 py-1.5 ${isOverdue ? 'text-red-600 font-bold' : 'text-[#333]'}`}>
                        {t.scheduled_date || '—'}
                      </td>
                      <td className="px-2 py-1.5 text-[#666] hidden sm:table-cell">{t.scheduled_time || '—'}</td>
                      <td className="px-2 py-1.5 hidden sm:table-cell">
                        <span className={`inline-flex px-1.5 py-0.5 text-[9px] font-medium rounded-sm border capitalize ${PRIORITY_BADGES[t.priority] || PRIORITY_BADGES.medium}`}>
                          {t.priority}
                        </span>
                      </td>
                      <td className={`px-2 py-1.5 font-medium ${t.completed ? 'line-through text-[#999]' : 'text-[#333]'}`}>
                        {t.contact_id ? (
                          <button
                            onClick={() => openTaskContact(t)}
                            className="text-left hover:underline hover:text-[#0B5394] focus:outline-none focus:underline"
                            data-testid={`task-open-${t.activity_id}`}
                            title="Open contact (Lookup will be this task list)"
                          >
                            {t.title}
                          </button>
                        ) : (
                          t.title
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-[#0B5394] hidden md:table-cell">
                        {t.contact_id && t.contact_name ? (
                          <button
                            onClick={() => openTaskContact(t)}
                            className="hover:underline focus:outline-none focus:underline"
                            data-testid={`task-contact-${t.activity_id}`}
                          >
                            {t.contact_name}
                          </button>
                        ) : (
                          t.contact_name || '—'
                        )}
                      </td>
                      <td className="px-1 py-1.5">
                        <button onClick={() => deleteTask(t.activity_id)} className="text-[#C0392B] hover:bg-red-50 p-0.5" data-testid={`task-delete-${t.activity_id}`}>
                          <Trash size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <div className="bg-[#E0E0E0] border-t border-[#999] px-3 py-1 flex items-center justify-between text-[10px] text-[#666] shrink-0">
        <span>{tasks.length} tasks shown</span>
        <span>{pendingCount} pending | {overdueCount} overdue</span>
      </div>

      {/* New Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[440px] rounded-none border-[#999] p-0 gap-0" data-testid="new-task-dialog">
          <DialogHeader className="px-4 pt-3 pb-2 bg-[#E0E0E0] border-b border-[#999]">
            <DialogTitle className="text-[13px] font-bold text-[#333]">New Task</DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-3">
            <div className="flex gap-1">
              {['todo','call','meeting','email','note'].map(type => {
                const Icon = TYPE_ICONS[type];
                return (
                  <button key={type} onClick={() => setForm(f => ({ ...f, activity_type: type }))}
                    className={`flex items-center gap-1 px-2 py-1 text-[10px] font-medium border ${form.activity_type === type ? 'bg-[#0B5394] text-white border-[#0B5394]' : 'border-[#B0B0B0] text-[#555] hover:bg-[#E8E8E8]'}`}
                    data-testid={`task-type-${type}`}
                  ><Icon size={11} weight="bold" /><span className="capitalize">{type === 'todo' ? 'To-Do' : type}</span></button>
                );
              })}
            </div>
            <input autoFocus value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Task title..."
              autoComplete="one-time-code" className="w-full h-[28px] px-2 text-[12px] border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394]" data-testid="task-title-input" />
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] font-medium text-[#666]">Date</label>
                <DatePicker
                  value={form.scheduled_date}
                  onChange={(v) => setForm(f => ({ ...f, scheduled_date: v }))}
                  testId="task-date"
                  height="md"
                />
              </div>
              <div>
                <label className="text-[10px] font-medium text-[#666]">Time</label>
                <TimePicker
                  value={form.scheduled_time}
                  onChange={(v) => setForm(f => ({ ...f, scheduled_time: v }))}
                  testId="task-time-input"
                  height="md"
                />
              </div>
              <div>
                <label className="text-[10px] font-medium text-[#666]">Priority</label>
                <select value={form.priority} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))}
                  className="w-full h-[28px] px-1 text-[11px] border border-[#B0B0B0] mt-0.5" data-testid="task-priority-input">
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-medium text-[#666]">Contact</label>
              {selectedContact ? (
                <div className="flex items-center gap-2 p-1.5 bg-[#E8E8E8] border border-[#B0B0B0] mt-0.5">
                  <User size={11} className="text-[#666]" />
                  <span className="text-[11px] font-medium flex-1">{selectedContact.first_name} {selectedContact.last_name}</span>
                  <button onClick={() => { setSelectedContact(null); setForm(f => ({ ...f, contact_id: '' })); }} className="text-[10px] text-[#C0392B]">Remove</button>
                </div>
              ) : (
                <div>
                  <input value={contactSearch} onChange={(e) => searchContacts(e.target.value)} placeholder="Search..." autoComplete="one-time-code"
                    className="w-full h-[26px] px-2 text-[11px] border border-[#B0B0B0] mt-0.5 focus:outline-none focus:border-[#0B5394]" data-testid="task-contact-search" />
                  {contactSearch && contactSearch.length === 1 && (
                    <p className="mt-1 text-[10px] text-[#888] italic" data-testid="task-contact-hint">Keep typing — searching after 2 characters.</p>
                  )}
                  {contactSearch && contactSearch.length >= 2 && contactResults.length === 0 && (
                    <p className="mt-1 text-[10px] text-[#C0392B]" data-testid="task-contact-no-results">
                      No contacts match "{contactSearch}".
                    </p>
                  )}
                  {contactResults.length > 0 && (
                    <div className="mt-1 border border-[#B0B0B0] bg-white max-h-24 overflow-y-auto">
                      {contactResults.map(c => (
                        <button key={c.contact_id} onClick={() => { setSelectedContact(c); setForm(f => ({ ...f, contact_id: c.contact_id })); setContactResults([]); setContactSearch(''); }}
                          className="w-full text-left px-2 py-1 text-[11px] hover:bg-[#E8E8E8]">{c.first_name} {c.last_name} {c.company && <span className="text-[#999]">({c.company})</span>}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <textarea value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))} rows={2} placeholder="Notes..."
              className="w-full px-2 py-1 text-[11px] border border-[#B0B0B0] focus:outline-none focus:border-[#0B5394] resize-none" data-testid="task-notes-input" />
          </div>
          <div className="flex justify-end px-4 py-2 bg-[#E0E0E0] border-t border-[#999]">
            <button onClick={handleCreate} disabled={saving || !form.title.trim()}
              className="px-3 py-1 text-[11px] font-medium bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50" data-testid="save-task-btn">
              {saving ? '...' : 'Create Task'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
